import { SecShape, type SecondaryDef } from '../../sim/secondary';

/** S07 — TRAQUEURS. La première arme du catalogue qu'on tire SANS VISER, et
 *  c'est tout ce qu'elle a à offrir.
 *
 *  ── Le besoin ────────────────────────────────────────────────────────────
 *  Les six premières armes ont un point commun que personne n'avait relevé
 *  parce qu'il était universel : elles exigent toutes d'être ALIGNÉ avant de
 *  tirer. La Lance veut une file au-dessus de soi, le Faisceau une ligne qu'on
 *  tient, les Flanqueurs un ennemi sur le côté, les Mines une vague anticipée.
 *  Or il existe une situation, la plus fréquente du jeu, où le joueur n'a
 *  précisément pas le loisir de s'aligner : celle où il esquive. Pendant ces
 *  secondes-là — les plus longues d'une partie — le clic droit ne sert à rien,
 *  quelle que soit l'arme emportée, parce qu'aucune ne pardonne de ne pas
 *  regarder où l'on tire.
 *
 *  Le Traqueur convertit de l'ATTENTION en dégâts. On le déclenche vers le haut
 *  sans lever les yeux du rideau, et il trouve. C'est la seule arme qu'on peut
 *  jouer en regardant sa propre hitbox.
 *
 *  ── Ce qu'il ne doit surtout pas devenir ─────────────────────────────────
 *  Un guidage sans borne supprime la visée, donc la seule décision offensive du
 *  jeu, donc le jeu : le clic droit deviendrait un bouton « infliger des
 *  dégâts » et le joueur cesserait de choisir quand le presser. Deux bornes
 *  l'en empêchent, et elles sont dans le moteur parce qu'elles DÉFINISSENT
 *  l'arme au lieu de la régler :
 *
 *    · un cône d'acquisition de 55° — le missile ne va que vers ce qui est
 *      déjà devant lui. Envoyé du mauvais côté, il ne revient pas ;
 *    · une portée de verrouillage de 520 u — il ne voit pas depuis le bas du
 *      terrain. Il faut l'avoir lancé dans la bonne moitié.
 *
 *  Le joueur décide donc toujours de la DIRECTION ; le guidage ne rattrape que
 *  l'erreur finale. C'est la différence entre « je n'ai pas eu à viser
 *  précisément » et « je n'ai pas eu à viser ».
 *
 *  ── La rotation, mesurée et non devinée ──────────────────────────────────
 *  110 °/s, et ce n'est pas le premier chiffre essayé. Le banc a d'abord été
 *  monté avec un cône de 55° et 520 u de portée : le missile rattrapait alors
 *  une cible décalée de 520 u, c'est-à-dire à peu près n'importe quoi dans la
 *  moitié haute du terrain. L'arme fonctionnait parfaitement et ne demandait
 *  plus rien au joueur.
 *
 *  `tests/sim/arsenal.test.ts` borne désormais le résultat des DEUX CÔTÉS : il
 *  exige qu'un écart latéral de 220 u soit rattrapé — un tir droit le manque de
 *  très loin — et qu'un écart de 460 u ne le soit PAS. Sans la seconde
 *  exigence, la première serait satisfaite par un missile parfait, donc par la
 *  suppression du jeu ; et c'est toujours la seconde qu'on oublie d'écrire,
 *  parce qu'un test qui vérifie qu'une arme ne marche pas ressemble à un test
 *  inutile.
 *
 *  ── L'équilibrage, et pourquoi il est le plus bas du catalogue ───────────
 *  38 DPS nominaux, 19 % de la primaire, moitié moins que les Flanqueurs qui
 *  étaient jusqu'ici l'arme la plus modeste. Ce n'est pas de la prudence : le
 *  DPS nominal suppose que tout touche, hypothèse fausse pour toutes les autres
 *  armes et VRAIE pour celle-ci. Un traqueur à 120 DPS nominaux vaudrait trois
 *  fois la Lance sur le terrain tout en paraissant deux fois plus faible sur le
 *  papier. Le garde-fou le sait désormais (`secondaryDpsCap`), et la mesure
 *  réelle est vérifiée dans un monde qui tourne.
 *
 *  ── ⚠️ Nom de fichier ────────────────────────────────────────────────────
 *  Il doit se trier APRÈS `lance.def.ts` : le registre numérote dans l'ordre
 *  alphabétique des chemins et `Snapshot` mêle l'index de l'arme équipée à
 *  l'empreinte de replay. Voir l'avertissement complet dans `trainee.def.ts`. */
const traqueurs: SecondaryDef = {
  id: 'missiles-traqueurs',
  name: 'Missiles traqueurs',
  shape: SecShape.Traqueur,
  cooldown: 5.5,
  shots: 1,
  shotEvery: 0,
  count: 3,
  // Écartement des tubes. Modeste : ce sont les angles de départ qui ouvrent la
  // salve, pas la position des bouches.
  spacing: 30,
  // ±12°. L'éventail sert la LISIBILITÉ du guidage avant de servir la
  // couverture : trois missiles lancés parallèles convergeraient sans qu'on voie
  // qu'ils corrigent, et l'arme passerait pour un tir groupé un peu lent. Écartés
  // puis refermés, ils dessinent leur propre mécanique.
  //
  // ⚠️ L'ouverture participe à la PORTÉE LATÉRALE de l'arme, ce qui n'est pas
  // évident : le missile extérieur part déjà tourné vers le côté, donc son cône
  // d'acquisition balaie plus loin que celui du missile central. Élargir
  // l'éventail « pour la lisibilité » desserre donc en douce le garde-fou de
  // guidage — mesuré, pas supposé.
  spreadDeg: 24,
  // Lent, et c'est une condition d'existence : un missile rapide traverse le
  // terrain avant d'avoir pu tourner de dix degrés. Le guidage a besoin de temps
  // de vol, donc de lenteur. À 620 u/s il reste très au-dessus des projectiles
  // ennemis (380-620), donc la hiérarchie de lecture tient.
  speed: 620,
  radius: 13,
  damage: 70,
  // 110 °/s. Mesuré, borné des deux côtés — voir l'en-tête.
  turnDeg: 110,
  text: '3 × 70 dégâts guidés, sans viser — 5,5 s',
};

export default traqueurs;
